import type { AcceptanceRollbackFromStatus, CampaignPipeline, PostStatus } from '@discovr/types';

const ALL_STAGES: CampaignPipeline = {
  script_enabled: true,
  content_enabled: true,
  brand_safety_enabled: true,
};

/** A campaign with no pipeline config runs every stage. */
export function resolvePipeline(pipeline: CampaignPipeline | null | undefined): CampaignPipeline {
  return pipeline ?? ALL_STAGES;
}

/**
 * True when the URL names a script/content tab the nav dropped because the
 * campaign's pipeline has that stage off. Such a deep link is a 404; tabs hidden
 * for any other reason keep their existing fallback.
 */
export function isDisabledLaneTab(
  tab: string,
  navItems: ReadonlyArray<{ id: string }>,
  pipeline: CampaignPipeline | null | undefined,
): boolean {
  const p = resolvePipeline(pipeline);
  const laneOff = (tab === 'scripts' && !p.script_enabled) || (tab === 'content' && !p.content_enabled);
  return laneOff && !navItems.some((item) => item.id === tab);
}

/** The insights drawer tab to show; brand safety falls back to overview when off. */
export function resolveInsightsTab<T extends string>(
  tab: T,
  pipeline: CampaignPipeline | null | undefined,
): T | 'overview' {
  return tab === 'brand-safety' && !resolvePipeline(pipeline).brand_safety_enabled ? 'overview' : tab;
}

/** Creators currently working in each optional lane; a lane can only be turned off at zero. */
export function laneBlockers(
  byStatus: Partial<Record<PostStatus, number>> | undefined,
): { script: number; content: number } {
  const n = (key: PostStatus) => byStatus?.[key] ?? 0;
  return {
    script: n('script_pending') + n('script_review'),
    content: n('content_pending') + n('content_review'),
  };
}

export type QuickActionStage = 'script' | 'content' | 'live';

/** The first stage a pending creator can be fast-tracked into. */
export function quickActionStage(pipeline: CampaignPipeline | null | undefined): QuickActionStage {
  const p = resolvePipeline(pipeline);
  if (p.script_enabled) return 'script';
  return p.content_enabled ? 'content' : 'live';
}

/** The status a freshly confirmed creator's post starts in (mirrors the backend's pipeline.Initial()). */
export function initialPostStatus(pipeline: CampaignPipeline | null | undefined): AcceptanceRollbackFromStatus {
  return `${quickActionStage(pipeline)}_pending`;
}
